/**
 * Workspace Context
 *
 * Manages workspace selection and provides workspace data.
 * Internally uses TanStack Query for data fetching with automatic caching.
 *
 * This provides backward compatibility with existing components while
 * leveraging TanStack Query benefits (caching, deduplication, background refetch).
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiService } from '../services/api.service';
import { getCurrentUser } from '../services/api/users.api';
import {
  createWorkspace as createWorkspaceApi,
  getWorkspace,
  getWorkspaces,
} from '../services/api/workspace.api';
import { useAuth } from './AuthContext';
import { safeAnalytics } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import { queryKeys, clearWorkspaceQueries } from '../lib/queryClient';
import { subscribeWorkspaceUnavailable } from '../services/events/workspaceUnavailableEvents';
import { getWorkspaceId, setWorkspaceId as setGlobalWorkspaceId } from '../utils/workspaceState';
import {
  isWorkspaceSwitchSupersededError,
  WorkspaceSessionSupersededError,
  WorkspaceSwitchContractError,
  WorkspaceSwitchSupersededError,
} from '../utils/workspaceSwitch';
import type { WorkspaceResearchContext } from '@bower/shared';
import type { UserProperties } from '../types/analytics';
import { clearInMemoryDrafts } from '../stores/inMemoryDrafts';

// Re-export for backward compatibility
export { getWorkspaceId };

// The raw (uncollapsed) Prisma role values `Workspace.userRole` can carry —
// see `workspace.service.ts`'s `userRole: memberships[0]?.role`. Validated
// against here (not just lowercased blindly) so a future backend role value
// this list doesn't know about degrades to `undefined` rather than shipping
// an unvalidated string as `UserProperties['workspace_role']`. Cannot import
// the shared `normaliseWorkspaceRole` from `hooks/user/useWorkspaceRole.ts`
// here — that module imports `useWorkspace` FROM this file, so pulling it in
// here would be circular.
const ANALYTICS_WORKSPACE_ROLES: ReadonlySet<string> = new Set([
  'OWNER',
  'ADMIN',
  'MEMBER',
  'GUEST',
  'VIEWER',
]);

function toAnalyticsWorkspaceRole(raw: string | undefined): UserProperties['workspace_role'] {
  if (!raw) return undefined;
  const upper = raw.toUpperCase();
  if (!ANALYTICS_WORKSPACE_ROLES.has(upper)) return undefined;
  return upper.toLowerCase() as UserProperties['workspace_role'];
}

const WORKSPACE_ID_KEY = 'workspaceId';
const WORKSPACE_DATA_KEY = 'workspaceData';
/**
 * Stamps the active-workspace pin with the user it belongs to.
 *
 * `workspaceId` lives in localStorage, which is SHARED by every tab on the
 * origin — so the pin is owned by a user, and only a DIFFERENT user signing in
 * may clear it. The auth-transition effect below used to clear it on any auth
 * *loading* transition, and `bootLoading` starts `true` on every page load, so
 * EVERY new tab or reload wiped the pin for every other tab. That was invisible
 * while all tabs sat in one workspace (the surviving tab rewrote the same
 * value) and destructive the moment one tab switched: the others lost their pin
 * and bounced to the login screen. Reproduced against production 2026-08-18 —
 * a second tab booting cleared the first tab's pin within 300ms.
 */
const WORKSPACE_OWNER_KEY = 'workspaceOwnerId';

function clearWorkspacePin(): void {
  localStorage.removeItem(WORKSPACE_ID_KEY);
  localStorage.removeItem(WORKSPACE_DATA_KEY);
  localStorage.removeItem(WORKSPACE_OWNER_KEY);
}

/**
 * True only when the stored pin PROVABLY belongs to someone else.
 *
 * Fails toward keeping the pin: an unknown identity (signed out, or auth still
 * hydrating) and an unstamped legacy pin both return `false`. Neither is a
 * hole — a real sign-out clears the pin through `AuthContext`, the
 * membership-validation effect below re-points a pin the user can't access,
 * and the backend enforces membership on every workspace-scoped request
 * regardless of what this header claims.
 */
function pinBelongsToAnotherUser(ownerIdentity: string | null): boolean {
  if (!ownerIdentity) return false;
  const owner = localStorage.getItem(WORKSPACE_OWNER_KEY);
  if (!owner) return false;
  return owner !== ownerIdentity;
}

/**
 * The stored pin, but only when it PROVABLY belongs to this user.
 *
 * The strict mirror of `pinBelongsToAnotherUser`, and deliberately not its
 * negation: that helper answers "may I clear this?" and so treats an unknown
 * owner as "leave it alone", while this one answers "may I ADOPT this?" and so
 * treats an unknown owner as "no". Both fail toward not acting on a pin whose
 * owner is uncertain, which is the only reading under which an unstamped legacy
 * pin is safe in both directions.
 */
export function pinOwnedBy(ownerIdentity: string | null): string | null {
  if (!ownerIdentity) return null;
  if (localStorage.getItem(WORKSPACE_OWNER_KEY) !== ownerIdentity) return null;
  return localStorage.getItem(WORKSPACE_ID_KEY);
}

export interface Workspace {
  id: string;
  name: string;
  institution?: string;
  description?: string;
  /**
   * Creator's user id. MUST stay named `createdById` — that is the
   * field name the API actually returns (Prisma scalar `createdById`;
   * `createdBy` is the User *relation* and is never included in the
   * `/workspaces/:id` payload). This interface previously declared
   * `createdBy`, which silently resolved to `undefined` at runtime and
   * blanked the Manage-access dialog.
   */
  createdById: string;
  /** Current user's membership role. Optional for older cached payloads. */
  userRole?: string;
  /**
   * Content hash of the workspace's logo, null when it has none
   * (`WorkspaceLogo` then draws a monogram). On both the detail and list
   * payloads; optional for older cached ones.
   */
  logoHash?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Populated by the workspace detail AND list endpoints (the switcher
   * hands a list item to `setWorkspace`, so both must agree). Feeds the
   * `workspace_member_count` / `total_projects` / `total_notes` PostHog
   * person properties, the workspace group properties and the
   * `Workspace Switched` event.
   *
   * `noteCount` is live `note` artifacts only — NOT all artifacts. The
   * backend deliberately does not map `_count.artifacts` here, because that
   * counts attachments and every other artifact type too. Still optional:
   * cached `workspaceData` in localStorage predates this field.
   */
  stats?: {
    memberCount: number;
    projectCount: number;
    noteCount: number;
  };
  /**
   * Workspace settings passthrough. `workspaceContext` holds the
   * onboarding research profile (field/role/labSize) used to tailor the
   * first-run hero + suggested prompts. Optional — older workspaces and
   * the list endpoint may omit it.
   */
  settings?: {
    workspaceContext?: WorkspaceResearchContext;
    [key: string]: unknown;
  };
}

interface WorkspaceContextType {
  // Current workspace (null during initialization or if none selected)
  workspace: Workspace | null;

  // Current workspace ID (available immediately from localStorage, even before full workspace loads)
  workspaceId: string | null;

  // List of workspaces user has access to
  availableWorkspaces: Workspace[];

  // Loading states
  isLoading: boolean;
  // Loading state of the availableWorkspaces LIST query specifically (distinct
  // from `isLoading`, which tracks the pinned-workspace DETAIL query). Callers
  // that branch on membership (e.g. onboarding's invited-vs-new classification)
  // must wait on THIS, not `isLoading` — the detail query can settle while the
  // list is still in flight.
  availableWorkspacesLoading: boolean;
  isInitializing: boolean;

  // Error state
  error: string | null;

  // Whether user needs to create their first workspace
  needsWorkspaceCreation: boolean;

  // Actions
  setWorkspace: (workspace: Workspace) => void;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  refreshWorkspace: () => Promise<void>;
  clearWorkspace: () => void;
  createWorkspace: (data: {
    name: string;
    institution?: string;
    description?: string;
    /** Onboarding wizard v2 only — triggers workspace seeding. */
    seedOnboarding?: boolean;
    /** WORKSPACE research context only (field/lab-size); role is personal. */
    onboardingProfile?: WorkspaceResearchContext;
  }) => Promise<Workspace>;
}

/**
 * Runtime guard for the workspace payload.
 *
 * Why this exists: `Workspace` above is a hand-written claim about what
 * the API returns, and `response.data as Workspace` makes the compiler
 * believe it unconditionally. When the claim was wrong — the interface
 * said `createdBy`, the API sends `createdById` — the mismatch surfaced
 * as `undefined` at one read site and silently blanked the Manage-access
 * dialog in production. Every unit test stayed green because the mocks
 * were authored from the same wrong interface.
 *
 * A cast can't catch that; only reading the actual bytes can. This
 * schema covers the fields the app dereferences, and is deliberately:
 *   - **passthrough** — unknown/extra server fields are fine, never an error
 *   - **non-throwing by default** — background reads degrade to a loud log,
 *     never a white screen. Explicit workspace switches opt into strict mode
 *     because malformed target state must not cross the commit boundary.
 */
const workspaceShape = z.looseObject({
  id: z.string(),
  name: z.string(),
  createdById: z.string(),
});

// Log each offending workspace once per session — a failing shape would
// otherwise re-log on every background refetch. Module-scoped, so it also
// persists across tests in a file: a second malformed-payload test for the
// same workspace id would silently observe ZERO calls and look like a broken
// assertion. Tests must call `__resetShapeWarnCache()` in `beforeEach`.
const shapeWarned = new Set<string>();

/**
 * Test-only: clear the once-per-session dedup so each test starts clean.
 *
 * @internal
 */
export function __resetShapeWarnCache(): void {
  shapeWarned.clear();
}

function validateWorkspaceShape(
  data: unknown,
  source: string,
  options: { strict?: boolean } = {},
): Workspace {
  const result = workspaceShape.safeParse(data);
  if (!result.success) {
    const id = (data as { id?: string } | null)?.id ?? 'unknown';
    if (!shapeWarned.has(id)) {
      shapeWarned.add(id);
      console.error(
        `[WorkspaceContext] workspace payload from ${source} does not match the expected shape. ` +
          'UI that reads the missing fields will silently degrade',
        { workspaceId: id, issues: result.error.issues },
      );
    }
    if (options.strict) {
      throw new Error('Invalid workspace payload');
    }
  }
  return data as Workspace;
}

/**
 * Same guard for the LIST payloads. These matter as much as the detail
 * fetch: the auto-recovery paths call `setWorkspace(availableWorkspaces[0])`
 * / `setWorkspace(workspaces[0])`, so a list element becomes the context
 * workspace and flows into every consumer — validating only the detail
 * fetch would leave the guard unenforced on half its targets.
 */
function validateWorkspaceListShape(data: unknown, source: string): Workspace[] {
  if (!Array.isArray(data)) return [];
  data.forEach((item) => validateWorkspaceShape(item, source));
  return data as Workspace[];
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

// Simple ID-only hooks for components that just need the ID
// Returns the workspace ID from state (available immediately from localStorage)
// This avoids race conditions where workspace object hasn't loaded yet
export function useWorkspaceId(): string | null {
  const context = useContext(WorkspaceContext);
  // Use the stored workspaceId, not workspace?.id, to avoid race conditions
  // where the ID is known but the full workspace object hasn't loaded yet
  return context?.workspaceId || null;
}

export function useWorkspaceInitializing(): boolean {
  const context = useContext(WorkspaceContext);
  return context?.isInitializing ?? true;
}

// Full workspace hook (backward compatible)
export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const { user, bootLoading, actionLoading } = useAuth();
  const authLoading = bootLoading || actionLoading;
  const authIdentity = user?.internalId || user?.uid || null;
  /**
   * Ownership key for the shared workspace pin — deliberately NOT
   * `authIdentity`. `internalId` is filled in by a best-effort backend
   * enrichment call (`auth.service.ts`'s `signInWithPassword` /
   * `onAuthStateChanged`) that can transiently fail on one login and succeed
   * on the next; `authIdentity` falls back to `uid` whenever it has. So the
   * SAME returning user can authenticate with `authIdentity` reading as their
   * `internalId` (stamped on a prior session) one moment and their `uid` the
   * next, on nothing worse than a network blip. `pinBelongsToAnotherUser`
   * compares by strict equality, so gating it on `authIdentity` would treat
   * that churn as a different person and wipe the user's own pin — a milder
   * recurrence of the exact bug this file exists to fix. `uid` never has this
   * problem: Firebase populates it synchronously and it never changes for the
   * lifetime of the account, so it is what the pin's ownership stamp compares
   * against.
   */
  const workspaceOwnerIdentity = user?.uid ?? null;
  const queryClient = useQueryClient();
  const switchGenerationRef = useRef(0);
  const authSessionGenerationRef = useRef(0);
  const authSessionRef = useRef({ identity: authIdentity, loading: authLoading });
  const authSessionMatchesRender =
    authSessionRef.current.identity === authIdentity &&
    authSessionRef.current.loading === authLoading;

  // Local state for workspace ID (persisted to localStorage)
  const [workspaceId, setWorkspaceIdState] = useState<string | null>(() => {
    return localStorage.getItem('workspaceId');
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const [needsWorkspaceCreation, setNeedsWorkspaceCreation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidateWorkspaceSwitches = useCallback(() => {
    switchGenerationRef.current += 1;
  }, []);

  useLayoutEffect(
    () => () => {
      invalidateWorkspaceSwitches();
    },
    [invalidateWorkspaceSwitches],
  );

  // Never let a prior user's workspace survive an auth transition. Query
  // `enabled` only stops network requests; it does not hide cached results.
  // Masking below closes the render-time seam, while this layout effect clears
  // the global request header and cache before the browser can paint it.
  useLayoutEffect(() => {
    const previousAuthSession = authSessionRef.current;
    const identityChanged = previousAuthSession.identity !== authIdentity;
    const authSessionChanged = identityChanged || previousAuthSession.loading !== authLoading;
    if (authSessionChanged) {
      authSessionGenerationRef.current += 1;
      invalidateWorkspaceSwitches();
    }
    authSessionRef.current = { identity: authIdentity, loading: authLoading };
    // Typed drafts kept in memory belong to the user who typed them: none of
    // sign-out, the idle auto-logout or an account switch reloads the page.
    // Identity only, not loading churn, so a token refresh keeps a draft.
    if (identityChanged) clearInMemoryDrafts();

    if (!identityChanged && !authLoading) return;

    setWorkspaceIdState(null);
    setGlobalWorkspaceId(null);
    setIsInitializing(true);
    setNeedsWorkspaceCreation(false);
    setError(null);
    // Everything above is tab-local and safe to reset on any auth transition.
    // The pin below is NOT: localStorage is shared, so clearing it here reaches
    // into every other tab. Only do it when the pin provably belongs to another
    // user — see `pinBelongsToAnotherUser`. This tab re-reads the surviving pin
    // during re-initialisation, which is what lets a tab whose auth state
    // merely churned land back on the workspace it was already showing.
    if (pinBelongsToAnotherUser(workspaceOwnerIdentity)) {
      clearWorkspacePin();
    }
    queryClient.clear();
  }, [authIdentity, authLoading, workspaceOwnerIdentity, invalidateWorkspaceSwitches, queryClient]);

  // Fetch workspace data using TanStack Query
  const {
    data: workspace,
    isLoading: workspaceLoading,
    refetch: refetchWorkspace,
  } = useQuery({
    queryKey: queryKeys.workspaces.detail(workspaceId || ''),
    queryFn: async () => {
      if (!workspaceId) return null;
      const response = await getWorkspace(apiService, workspaceId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch workspace');
      }
      return validateWorkspaceShape(response.data, 'GET /workspaces/:id');
    },
    enabled: !!workspaceId && !!user && !authLoading && authSessionMatchesRender,
    staleTime: 5 * 60 * 1000, // Workspace data doesn't change often
    // Pinned workspace failing access checks is permanent for this
    // session — retrying just delays the fallback. The validation
    // effect below detects the mismatch (via availableWorkspaces)
    // and switches to a workspace the user can actually access.
    retry: false,
  });

  // Fetch available workspaces
  const {
    data: availableWorkspaces = [],
    isLoading: availableWorkspacesLoading,
    refetch: refetchWorkspaces,
  } = useQuery({
    queryKey: queryKeys.workspaces.all,
    queryFn: async () => {
      const response = await getWorkspaces(apiService);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch workspaces');
      }
      return validateWorkspaceListShape(response.data, 'GET /workspaces');
    },
    enabled: !!user && !authLoading && authSessionMatchesRender,
    staleTime: 5 * 60 * 1000,
  });

  // Update global workspace ID when it changes
  useEffect(() => {
    if (authLoading || !authSessionMatchesRender) return;
    setGlobalWorkspaceId(workspaceId);
    if (workspaceId) {
      localStorage.setItem(WORKSPACE_ID_KEY, workspaceId);
      // Stamp the owner alongside the pin so a later auth transition can tell
      // "same user, new tab" from "different user on this browser". Uses
      // `workspaceOwnerIdentity`, not `authIdentity` — see its definition.
      if (workspaceOwnerIdentity) {
        localStorage.setItem(WORKSPACE_OWNER_KEY, workspaceOwnerIdentity);
      }
      if (workspace) {
        localStorage.setItem(WORKSPACE_DATA_KEY, JSON.stringify(workspace));
      }
    }
  }, [authLoading, authSessionMatchesRender, workspaceId, workspace, workspaceOwnerIdentity]);

  // Set workspace (updates ID and triggers query refetch)
  const setWorkspace = useCallback(
    (ws: Workspace) => {
      invalidateWorkspaceSwitches();
      setWorkspaceIdState(ws.id);
      setGlobalWorkspaceId(ws.id);
      setError(null);
      setNeedsWorkspaceCreation(false);

      // Update cache immediately
      queryClient.setQueryData(queryKeys.workspaces.detail(ws.id), ws);

      // Track analytics
      try {
        safeAnalytics.identifyUser({
          workspace_id: ws.id,
          workspace_role: toAnalyticsWorkspaceRole(ws.userRole),
          workspace_member_count: ws.stats?.memberCount,
          total_projects: ws.stats?.projectCount,
          total_notes: ws.stats?.noteCount,
          user_id: user?.internalId || user?.uid || '',
          email: user?.email || '',
          display_name: user?.bowerName || user?.displayName || undefined,
          is_internal: user?.internalAdmin === true,
        });

        // Super properties ride on EVERY later event, so ids only: a workspace
        // name is user content and PostHog receives behavior, not content.
        safeAnalytics.registerContext({ workspace_id: ws.id });

        // PostHog group analytics for workspace-level insights and cohort filtering
        safeAnalytics.setWorkspaceGroup(ws.id, {
          member_count: ws.stats?.memberCount,
          project_count: ws.stats?.projectCount,
          note_count: ws.stats?.noteCount,
          is_test_workspace: false,
        });

        safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_SWITCHED, {
          workspace_id: ws.id,
          member_count: ws.stats?.memberCount,
          timestamp: new Date().toISOString(),
        } as any);
      } catch (err) {
        console.warn('Failed to track workspace analytics:', err);
      }
    },
    [invalidateWorkspaceSwitches, user, queryClient],
  );

  // Clear workspace
  const clearWorkspace = useCallback(() => {
    invalidateWorkspaceSwitches();
    setWorkspaceIdState(null);
    setGlobalWorkspaceId(null);
    setError(null);
    clearWorkspacePin();
    clearWorkspaceQueries();
  }, [invalidateWorkspaceSwitches]);

  // Validate the pinned workspaceId against the user's actual
  // memberships once both queries have resolved. If the pinned
  // workspace isn't one the user belongs to — typically because
  // they were just removed from it (incident 2026-05-13), or
  // because localStorage carried over a workspace from a previous
  // account — fall back to the first available workspace so the
  // session can recover. Without this, every API request 403s on
  // the stale `x-workspace-id` header and the spinner runs forever.
  //
  // Two trigger conditions:
  //   1. `workspaceError` fires (the pinned-workspace fetch 403'd)
  //   2. `availableWorkspaces` arrives without the pinned id in it
  // Both routes converge on the same fallback: pick a workspace the
  // user actually has access to. `retry: false` on the workspace
  // query above means a single failed fetch is enough to trip this.
  //
  // `authLoading` guard: `queryKeys.workspaces.all` is not user-
  // scoped (cache key is just `['workspaces']`). During an auth
  // transition (user A logs out, B logs in) the cached list could
  // briefly belong to A while user is already B. Skipping the
  // effect while auth is resolving prevents a cross-user bounce.
  useEffect(() => {
    if (!authSessionMatchesRender) return;
    if (authLoading) return;
    if (!user || !workspaceId) return;
    if (availableWorkspaces.length === 0) return;
    const isMember = availableWorkspaces.some((w) => w.id === workspaceId);
    if (isMember) return;
    // Pick the fallback. `setWorkspace` updates state + cache +
    // analytics in one call, so the next render is the recovered
    // state rather than an empty intermediate flash. Emit a
    // dedicated analytics event so auto-recovery is visible in
    // PostHog distinctly from a user-initiated switch — a spike
    // in this event signals the underlying removal/lockout bug
    // class is recurring.
    safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_AUTO_RECOVERED, {
      from_workspace_id: workspaceId,
      to_workspace_id: availableWorkspaces[0].id,
      available_count: availableWorkspaces.length,
    } as any);
    setWorkspace(availableWorkspaces[0]);
  }, [workspaceId, availableWorkspaces, user, setWorkspace, authLoading, authSessionMatchesRender]);

  // The backend refused the active workspace as gone or scheduled for
  // deletion (403 WORKSPACE_UNAVAILABLE, see api.service). The cached list
  // still has it (5-minute staleTime), so refetch: the server's list omits
  // it, and the effect above moves the user to a workspace they still have.
  // With none left (it was their only one), unpin it so the app shows the
  // "no workspace" state instead of 403ing on every request. Only on this
  // positive refusal: an empty list alone can be an auth transition.
  useEffect(
    () =>
      subscribeWorkspaceUnavailable(({ workspaceId: refused }) => {
        if (!workspaceId || (refused !== null && refused !== workspaceId)) return;
        void refetchWorkspaces().then(({ data }) => {
          if (data && data.length === 0) {
            safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_AUTO_RECOVERED, {
              from_workspace_id: workspaceId,
              to_workspace_id: null,
              available_count: 0,
            });
            clearWorkspace();
            setNeedsWorkspaceCreation(true);
          }
        });
      }),
    [workspaceId, refetchWorkspaces, clearWorkspace],
  );

  // Refresh workspace data
  const refreshWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    await refetchWorkspace();
  }, [workspaceId, refetchWorkspace]);

  // Switch to a different workspace
  const switchWorkspace = useCallback(
    async (newWorkspaceId: string) => {
      const switchGeneration = switchGenerationRef.current + 1;
      switchGenerationRef.current = switchGeneration;
      setError(null);

      try {
        const response = await getWorkspace(apiService, newWorkspaceId);
        if (switchGeneration !== switchGenerationRef.current) {
          throw new WorkspaceSwitchSupersededError();
        }

        if (response.success && response.data) {
          const validatedWorkspace = validateWorkspaceShape(response.data, 'switchWorkspace', {
            strict: true,
          });
          if (validatedWorkspace.id !== newWorkspaceId) {
            throw new WorkspaceSwitchContractError(
              'Workspace switch response ID did not match requested workspace',
            );
          }
          localStorage.setItem(WORKSPACE_ID_KEY, validatedWorkspace.id);
          if (workspaceOwnerIdentity) {
            localStorage.setItem(WORKSPACE_OWNER_KEY, workspaceOwnerIdentity);
          }
          clearWorkspaceQueries();
          setWorkspace(validatedWorkspace);
        } else {
          throw new Error(response.error?.message || 'Failed to switch workspace');
        }
      } catch (err: unknown) {
        if (isWorkspaceSwitchSupersededError(err)) {
          throw err;
        }
        if (switchGeneration !== switchGenerationRef.current) {
          throw new WorkspaceSwitchSupersededError();
        }

        const message = err instanceof Error ? err.message : 'Failed to switch workspace';
        console.error('Failed to switch workspace:', err);
        setError(message);
        throw err;
      }
    },
    [setWorkspace, workspaceOwnerIdentity],
  );

  // Create a new workspace
  const createWorkspace = useCallback(
    async (data: {
      name: string;
      institution?: string;
      description?: string;
    }): Promise<Workspace> => {
      const authSessionGeneration = authSessionGenerationRef.current;
      let committedWorkspaceId: string | undefined;
      const assertCurrentAuthSession = () => {
        if (authSessionGeneration !== authSessionGenerationRef.current) {
          throw new WorkspaceSessionSupersededError(committedWorkspaceId, authIdentity);
        }
      };
      setError(null);

      try {
        const response = await createWorkspaceApi(apiService, data);
        if (
          response.success &&
          typeof (response.data as { id?: unknown } | undefined)?.id === 'string'
        ) {
          committedWorkspaceId = (response.data as { id: string }).id;
        }
        assertCurrentAuthSession();

        if (response.success && response.data) {
          const newWorkspace = validateWorkspaceShape(response.data, 'createWorkspace');

          // Optimistically inject the new workspace into the cached
          // availableWorkspaces list BEFORE calling setWorkspace. The
          // membership-validation effect runs on the next render
          // (workspaceId is about to change to newWorkspace.id) and
          // would otherwise see the new id missing from the still-
          // stale list, mistake the user for "removed from this
          // workspace," and bounce them back to availableWorkspaces[0].
          queryClient.setQueryData<Workspace[]>(queryKeys.workspaces.all, (prev) =>
            prev?.some((w) => w.id === newWorkspace.id) ? prev : [...(prev ?? []), newWorkspace],
          );

          setWorkspace(newWorkspace);

          // Refresh workspaces list from the server so any other
          // server-derived fields (member counts, etc.) catch up.
          await refetchWorkspaces();
          assertCurrentAuthSession();

          return newWorkspace;
        }
        throw new Error(response.error?.message || 'Failed to create workspace');
      } catch (err: any) {
        assertCurrentAuthSession();
        console.error('Failed to create workspace:', err);
        setError(err.message || 'Failed to create workspace');
        throw err;
      }
    },
    [authIdentity, setWorkspace, refetchWorkspaces, queryClient],
  );

  // Initialize workspace on mount and when user changes
  useEffect(() => {
    const authSessionGeneration = authSessionGenerationRef.current;
    let cancelled = false;
    const isCurrentAuthSession = () =>
      !cancelled && authSessionGeneration === authSessionGenerationRef.current;

    const initializeWorkspace = async () => {
      // Wait for auth to finish loading before making any decisions
      // This prevents clearing workspace while Firebase is still resolving the user
      if (authLoading) {
        return; // Stay in initializing state until auth resolves
      }

      // Clear workspace only if auth finished loading AND there's no user (logged out)
      if (!user) {
        if (!isCurrentAuthSession()) return;
        clearWorkspace();
        setIsInitializing(false);
        return;
      }

      if (!isCurrentAuthSession()) return;
      setIsInitializing(true);
      setError(null);

      try {
        // If we already have a workspace ID from localStorage, just wait for query
        if (workspaceId) {
          if (!isCurrentAuthSession()) return;
          setIsInitializing(false);
          return;
        }

        // Re-read the pin that SURVIVED in localStorage, before falling back to
        // the profile.
        //
        // The auth-transition layout effect above calls `setWorkspaceIdState(null)`
        // on every boot: `bootLoading` starts `true`, so `authLoading` is true on
        // the first pass and its `!identityChanged && !authLoading` early return
        // never fires. It deliberately does NOT clear the localStorage pin — that
        // would reach into every other tab — and its comment says this tab
        // "re-reads the surviving pin during re-initialisation". Nothing re-read
        // it. `workspaceId` is null by the time control arrives here, so every
        // boot fell through to `lastLoggedInWorkspace` and then to `workspaces[0]`,
        // and the pin was outvoted by the profile on every single load.
        //
        // Invisible while a user's profile agrees with their pin, which is the
        // common case. Not invisible when the two disagree — it is why the staging
        // E2E bot, pinned to the E2E workspace, ran every browser spec in the EVAL
        // workspace that happens to sort first in `GET /workspaces` while the
        // node-side fixtures used the pinned one (2026-08-28 nightly).
        //
        // Gated on PROVEN ownership rather than on the pin merely existing: an
        // unstamped legacy pin, or one stamped for somebody else, keeps the old
        // fallback. So this can never hand user B the workspace user A left in
        // shared localStorage — the same fail-toward-safety direction as
        // `pinBelongsToAnotherUser`, read the other way round.
        const ownedPin = pinOwnedBy(workspaceOwnerIdentity);
        if (ownedPin) {
          setWorkspaceIdState(ownedPin);
          setIsInitializing(false);
          return;
        }

        // Try to load user's last workspace from their profile
        const userResponse = await getCurrentUser(apiService);
        if (!isCurrentAuthSession()) return;
        const lastWorkspaceId = (userResponse.data as any)?.lastLoggedInWorkspace;

        // Fetch all workspaces for user, and trust the profile's pin only while
        // it is still one of them: a pin to a workspace that was deleted, or is
        // scheduled for deletion, would land on a 403, and recovering from that
        // would re-read the same pin here and loop.
        const workspacesResponse = await getWorkspaces(apiService);
        if (!isCurrentAuthSession()) return;
        const workspaces = validateWorkspaceListShape(workspacesResponse.data, 'GET /workspaces');

        if (lastWorkspaceId && workspaces.some((w) => w.id === lastWorkspaceId)) {
          setWorkspaceIdState(lastWorkspaceId);
          setIsInitializing(false);
          return;
        }

        if (workspaces.length > 0) {
          setWorkspace(workspaces[0]);
        } else {
          setNeedsWorkspaceCreation(true);
        }
      } catch (err: any) {
        if (!isCurrentAuthSession()) return;
        console.error('Failed to initialize workspace:', err);
        setError(err.message || 'Failed to initialize workspace');
        setNeedsWorkspaceCreation(true);
      } finally {
        if (isCurrentAuthSession()) {
          setIsInitializing(false);
        }
      }
    };

    void initializeWorkspace();
    return () => {
      cancelled = true;
    };
  }, [
    user,
    authIdentity,
    authLoading,
    workspaceId,
    workspaceOwnerIdentity,
    setWorkspace,
    clearWorkspace,
  ]);

  const maskWorkspaceForAuthTransition = authLoading || !authSessionMatchesRender;
  const exposedWorkspace = maskWorkspaceForAuthTransition ? null : workspace || null;

  // Session replay must never record a PHI workspace's DOM: POSTHOG is
  // `baa_covered: false` in config/egress-policy.yaml, and $snapshot payloads
  // never round-trip through the backend, so `mayEgressContent` cannot see
  // them. Same source of truth as `useIsPhiWorkspace`, but pushed into the
  // analytics singleton so the gate binds the recorder rather than the UI.
  // A null workspace (unresolved, or masked mid auth transition) deliberately
  // CLOSES the gate for a signed-in user — see `isReplayAllowed`.
  useEffect(() => {
    safeAnalytics.setWorkspacePhi(
      exposedWorkspace ? exposedWorkspace.settings?.phi === true : null,
    );
  }, [exposedWorkspace]);

  const value: WorkspaceContextType = {
    workspace: exposedWorkspace,
    // Never expose a stale workspace identifier while auth is changing users.
    workspaceId: maskWorkspaceForAuthTransition ? null : workspaceId,
    availableWorkspaces: maskWorkspaceForAuthTransition ? [] : availableWorkspaces,
    isLoading: maskWorkspaceForAuthTransition || workspaceLoading,
    availableWorkspacesLoading: maskWorkspaceForAuthTransition || availableWorkspacesLoading,
    isInitializing: maskWorkspaceForAuthTransition || isInitializing,
    error,
    needsWorkspaceCreation,
    setWorkspace,
    switchWorkspace,
    refreshWorkspace,
    clearWorkspace,
    createWorkspace,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};
